import {
  Chip,
  IconButton,
  makeStyles,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { BubbleMetric } from '../BubbleMetric';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight } from '@material-ui/icons';
import { smScreen } from '../../lib/constants';

const checkOverflow = (element: HTMLDivElement): boolean => {
  return (
    element!.scrollHeight > element!.clientHeight ||
    element!.scrollWidth > element!.clientWidth
  );
};

export const TableLegend = (props: TableLegendProps) => {
  const {
    data,
    externalFilter,
    onChange,
    onClearFilter,
    showAll = true,
    value,
  } = props;
  const isMobile = useMediaQuery(smScreen);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [alignLegend, setAlignLegend] = useState<
    'flex-start' | 'flex-end' | 'center'
  >('center');
  const classes = useStyles(alignLegend, isMobile)();

  const legendRef = useRef<HTMLDivElement>(null);
  const element = legendRef!.current;

  const handleResize = () => {
    setWindowWidth(window.innerWidth);
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (element) {
      setScrollPosition(element.offsetLeft);
    }
  }, [element]);

  useEffect(() => {
    if (element) {
      const overflow = checkOverflow(element);
      setIsOverflowing(overflow);
      setAlignLegend(overflow ? 'flex-start' : 'center');
    }
  }, [data, windowWidth]);

  const handleChange = (item: string) => {
    onChange(item);
  };

  const handleScroll = (direction: 'right' | 'left') => {
    if (element) {
      const scrollAmount = direction === 'left' ? -200 : 200;
      let scrollToPosition = element.scrollLeft;

      if (direction === 'left') scrollToPosition += scrollAmount;

      if (direction === 'right') scrollToPosition += scrollAmount;

      setScrollPosition(scrollToPosition);
      setIsOverflowing(checkOverflow(element));

      element.scroll({
        left: scrollToPosition,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className={classes.container}>
      {externalFilter !== undefined && externalFilter.filterTerm ? (
        <div className={classes.filter}>
          <Typography variant="caption">Filtering By:</Typography>
          <Chip
            label={externalFilter.filterTerm || ''}
            onDelete={onClearFilter}
            size="medium"
            variant="outlined"
          />
        </div>
      ) : null}
      {isOverflowing && !isMobile && scrollPosition ? (
        <IconButton
          className={classes.iconButton}
          onClick={() => handleScroll('left')}
        >
          <ArrowLeft fontSize="large" />
        </IconButton>
      ) : null}
      {isMobile && data.length ? (
        <Select
          className={classes.select}
          fullWidth={isMobile}
          placeholder="Table Legend"
          onChange={(event: ChangeEvent<any>) =>
            handleChange(event.target.value)
          }
          value={value === '' ? 'All' : value}
        >
          <MenuItem value={'All'}>All</MenuItem>
          {data
            .sort((a, b) => ((a.count ?? 0) > (b.count ?? 0) ? -1 : 1))
            .map((legend, index) => (
              <MenuItem
                className={classes.menuItem}
                key={index}
                value={legend.id}
              >
                <div
                  className={classes.dot}
                  style={{ backgroundColor: legend.color }}
                />
                {legend.value}
              </MenuItem>
            ))}
        </Select>
      ) : (
        <div ref={legendRef} className={classes.legend}>
          {showAll && data.length ? (
            <BubbleMetric
              total={data.reduce((total, cur) => total + (cur?.count ?? 0), 0)}
              label="All"
              selectedValue={value}
              value="All"
              backgroundColor="grey"
              onSelect={handleChange}
            />
          ) : null}
          {data
            .sort((a, b) => ((a.count ?? 0) > (b.count ?? 0) ? -1 : 1))
            .map((d, index) => (
              <BubbleMetric
                key={index}
                total={d.count ?? 0}
                label={d.value.toString()}
                selectedValue={value}
                value={d.id}
                backgroundColor={d.color ?? 'grey'}
                onSelect={handleChange}
              />
            ))}
        </div>
      )}
      {isOverflowing && !isMobile ? (
        <IconButton
          className={classes.iconButton}
          onClick={() => handleScroll('right')}
        >
          <ArrowRight fontSize="large" />
        </IconButton>
      ) : null}
    </div>
  );
};
const useStyles = (
  align: 'flex-start' | 'flex-end' | 'center',
  isMobile: boolean
) =>
  makeStyles((theme) => ({
    container: {
      display: 'flex',
      flex: 1,
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: 'center',
      justifyContent: 'center',
    },
    dot: {
      width: 15,
      height: 15,
      borderRadius: 15,
    },
    filter: {
      display: 'inherit',
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    iconButton: {
      '&:hover': {
        backgroundColor: 'transparent',
        opacity: 0.75,
      },
    },
    legend: {
      display: 'flex',
      flex: 1,
      flexDirection: 'row',
      justifyContent: align,
      overflow: 'hidden',
    },
    menuItem: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    select: {
      '& .MuiSelect-select': {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing(1),
      },
    },
  }));
